import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "../components/ui/navigation-menu";
import { List, CaretRight, CaretDown, Translate, Handshake, Phone } from "@phosphor-icons/react";

// Service group title → slug for main service pages
const SERVICE_SLUG_MAP = {
  "digital marketing":          "digital-marketing",
  "it consulting":              "it-consulting",
  "it services":                "it-consulting",
  "ai & automation":            "ai-automation",
  "ai and automation":          "ai-automation",
  "ai & software automation":   "ai-automation",
};
const serviceSlugFor = (title = "") => {
  const k = title.toLowerCase().trim();
  return SERVICE_SLUG_MAP[k] || k.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
};
const slugify = (s = "") => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
import { useI18n } from "../i18n/I18nContext";
import { useSiteContent } from "../i18n/SiteContent";
import BecomePartnerDialog from "../widgets/BecomePartnerDialog";
import ConnectDialog from "../widgets/ConnectDialog";

const Logo = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center select-none">
    <img src="/logo.png" alt="OpSysDigital" className="h-12 md:h-14 w-auto" />
  </Link>
);

// Bilingual A/أ icon language toggle (matches user reference image)
function LangToggle({ lang, setLang, isRTL }) {
  const next = lang === "en" ? "ar" : "en";
  return (
    <button
      onClick={() => setLang(next)}
      data-testid="lang-toggle"
      aria-label={`Switch language to ${next === "ar" ? "Arabic" : "English"}`}
      className="relative h-10 w-10 rounded-md flex items-center justify-center group transition hover:bg-slate-100"
      title={next === "ar" ? "العربية" : "English"}
    >
      <Translate size={20} weight="duotone" style={{ color: "#0F2A6E" }} className="group-hover:opacity-80" />
      <span
        className="absolute -bottom-0.5 -right-0.5 text-[9px] font-bold rounded-sm px-1 py-px"
        style={{ background: "#1B6FE0", color: "#FFFFFF", letterSpacing: "0.05em" }}
      >
        {lang === "en" ? "EN" : "AR"}
      </span>
    </button>
  );
}

// Accordion group used inside the mobile Sheet — closed by default, only one open at a time
function MobileAccordion({ id, label, openId, setOpenId, children }) {
  const open = openId === id;
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpenId(open ? null : id)}
        data-testid={`mobile-accordion-${id}`}
        className="w-full flex items-center justify-between px-3 py-3 text-[14.5px] font-semibold text-[#0F2A6E]"
      >
        <span>{label}</span>
        <CaretDown size={13} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-2">{children}</div>}
    </div>
  );
}

function MobileNav({ navL, brandsItems, servicesGroups, lang, setLang, isRTL, scrollTo, onClose, openPartner }) {
  const [openId, setOpenId] = React.useState(null); // <-- closed by default
  return (
    <div className="mt-6 flex flex-col">
      <div className="mb-4 px-1">
        <Link to="/" onClick={onClose} className="inline-flex items-center">
          <img src="/logo.png" alt="OpSysDigital" className="h-10 w-auto" />
        </Link>
      </div>

      <MobileAccordion id="about" label={navL.about} openId={openId} setOpenId={setOpenId}>
        {[
          ["/who-we-are",    "About OpSys"],
          ["/our-core-team", "Our Core Team"],
          ["/why-choose-us", "Why Choose OpSys"],
        ].map(([to, label]) => (
          <Link
            key={to}
            to={to}
            data-testid={`mobile-about-${to.replace(/^\//,'')}`}
            onClick={onClose}
            className="block px-6 py-2 text-[13.5px] text-[#1C2B3A] hover:bg-blue-50"
          >
            {label}
          </Link>
        ))}
      </MobileAccordion>

      {brandsItems.length > 0 && (
        <MobileAccordion id="products" label={navL.products} count={brandsItems.length} openId={openId} setOpenId={setOpenId}>
          {brandsItems.map((b, i) => {
            const slug = (b.slug || (b.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
            return (
              <Link
                key={i}
                to={`/products/${slug}`}
                onClick={onClose}
                className="block px-6 py-2 text-[13.5px] text-[#1C2B3A] hover:bg-blue-50"
              >
                {b.name}
              </Link>
            );
          })}
        </MobileAccordion>
      )}

      {servicesGroups.length > 0 && (
        <MobileAccordion id="services" label={navL.services} openId={openId} setOpenId={setOpenId}>
          {servicesGroups.map((g, gi) => {
            const parentSlug = g.parent_slug || (g.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            return (
              <div key={gi} className="mb-2 last:mb-0">
                <Link
                  to={`/services/${parentSlug}`}
                  onClick={onClose}
                  data-testid={`mobile-service-group-${gi}`}
                  className="block px-6 py-2 text-[13px] font-semibold text-[#0F2A6E] hover:bg-blue-50"
                >
                  {g.title}
                </Link>
                {(g.items || []).map((it, i) => {
                  const isObj = typeof it === "object" && it !== null;
                  const label = isObj ? it.title : it;
                  const subSlug = isObj ? it.slug : label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
                  return (
                    <Link
                      key={i}
                      to={`/services/${parentSlug}/${subSlug}`}
                      onClick={onClose}
                      data-testid={`mobile-service-${gi}-${i}`}
                      className="block px-10 py-1.5 text-[13px] text-[#6B7A8D] hover:text-[#1B6FE0]"
                    >
                      {label}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </MobileAccordion>
      )}

      <button
        data-testid="mobile-nav-industries"
        onClick={() => { onClose(); scrollTo("industries"); }}
        className="w-full text-start px-3 py-3 text-[14.5px] font-semibold text-[#0F2A6E] border-b border-slate-100"
      >
        {navL.industries}
      </button>

      <Link
        to="/clients-portfolio"
        onClick={onClose}
        data-testid="mobile-nav-clients"
        className="block w-full text-start px-3 py-3 text-[14.5px] font-semibold text-[#0F2A6E] border-b border-slate-100"
      >
        {navL.clientsPortfolio}
      </Link>

      <div className="mt-4 px-3 flex items-center gap-3">
        <span className="text-[12.5px] text-[#6B7A8D]">Language</span>
        <LangToggle lang={lang} setLang={setLang} isRTL={isRTL} />
      </div>

      <Button
        data-testid="mobile-nav-cta"
        onClick={() => { onClose(); scrollTo("faq"); }}
        className="mt-4 mx-3 text-white items-center gap-2"
        style={{ background: "#1B6FE0" }}
      >
        <Phone size={14} weight="fill" />
        {navL.scheduleCall}
      </Button>
    </div>
  );
}

export default function Header({ activeSection }) {
  const { t, lang, setLang, isRTL } = useI18n();
  const { pg, pgList } = useSiteContent();

  const navL = {
    about:        pg("nav.about", t.nav.about),
    products:     pg("nav.products", "Products"),
    services:     pg("nav.services", t.nav.services),
    industries:   pg("nav.industries", t.nav.industries),
    scheduleCall: pg("nav.scheduleCall", "Connect with us"),
    whoWeAre:     pg("nav.whoWeAre", t.nav.whoWeAre),
    whatWeDo:     pg("nav.whatWeDo", "What we do"),
    whyChooseUs:  pg("nav.whyChooseUs", "Why choose us"),
    team:         pg("nav.team", t.nav.team),
    careers:      pg("nav.careers", t.nav.careers),
    clientsPortfolio: pg("nav.clientsPortfolio", "Clients Portfolio"),
  };
  const servicesGroups = pgList("services.groups", t.servicesMega.groups);
  const industriesItems = pgList("industries.items", t.industries.items);
  const [brandsItems, setBrandsItems] = useState([]);

  // Load brands from /api/brands so header dropdown matches the home Products section
  useEffect(() => {
    let c = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brands`)
      .then((r) => r.json())
      .then((j) => !c && setBrandsItems(Array.isArray(j) ? j : []))
      .catch(() => {});
    return () => { c = true; };
  }, []);

  const [open, setOpen] = useState(false);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  const linkCls = (id, extra = "") =>
    `relative text-[13.5px] font-medium tracking-[-0.005em] transition ${
      activeSection === id ? "text-[#1B6FE0]" : "text-[#0F2A6E] hover:text-[#1B6FE0]"
    } ${extra}`;

  // Keep nav trigger background constant (override every shadcn state) per user request
  const triggerCls = (id, extra = "") =>
    `${linkCls(id, extra)} !bg-transparent hover:!bg-transparent focus:!bg-transparent active:!bg-transparent data-[state=open]:!bg-transparent data-[state=open]:hover:!bg-transparent data-[state=open]:focus:!bg-transparent data-[active]:!bg-transparent focus-visible:!bg-transparent focus:!shadow-none focus-visible:!shadow-none focus:!outline-none focus-visible:!outline-none data-[state=open]:!text-[#1B6FE0] hover:!text-[#1B6FE0] focus:!text-[#1B6FE0]`;

  return (
    <header className="sticky top-0 z-40 w-full" data-testid="site-header">
      <div className={`bg-white/95 backdrop-blur transition-shadow ${scrolled ? "shadow-sm" : "border-b border-slate-100"}`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-[78px] flex items-center justify-between gap-4">
          <Logo />

          {/* Right-aligned nav + lang selector + CTA */}
          <div className="flex items-center gap-3">
            <NavigationMenu className="hidden lg:flex" data-testid="main-nav">
              <NavigationMenuList className="gap-1">
                {/* About — links to detail pages per user feedback */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger data-testid="nav-about" className={triggerCls("about")}>
                    {navL.about}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[240px] p-3">
                      {[
                        ["/who-we-are",     "About OpSys"],
                        ["/our-core-team",  "Our Core Team"],
                        ["/why-choose-us",  "Why Choose OpSys"],
                      ].map(([to, label]) => (
                        <Link
                          key={to}
                          to={to}
                          data-testid={`menu-about-${to.replace(/^\//,'')}`}
                          className="w-full flex items-center justify-between px-3 py-2.5 rounded-md hover:bg-blue-50 transition group"
                        >
                          <span className="text-sm font-medium text-[#0F2A6E] group-hover:text-[#1B6FE0]">{label}</span>
                          <CaretRight size={13} weight="bold" className={`text-slate-300 group-hover:text-[#1B6FE0] transition ${isRTL ? "rtl-flip" : ""}`} />
                        </Link>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Products — 9 brand sub-pages */}
                {brandsItems.length > 0 && (
                  <NavigationMenuItem>
                    <NavigationMenuTrigger data-testid="nav-products" className={triggerCls("products")}>
                      {navL.products}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-[420px] p-3 grid grid-cols-2 gap-1">
                        {brandsItems.map((b, i) => {
                          const slug = (b.slug || (b.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
                          return (
                            <Link
                              key={i}
                              data-testid={`menu-product-${i}`}
                              to={`/products/${slug}`}
                              onClick={() => setOpen(false)}
                              className="text-start p-2.5 rounded-md hover:bg-blue-50 transition"
                            >
                              <div className="text-[13px] font-medium text-[#0F2A6E]">{b.name}</div>
                              {b.tagline && <div className="text-[11.5px] text-slate-500 mt-0.5 truncate">{b.tagline}</div>}
                            </Link>
                          );
                        })}
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                )}

                {/* Services mega-menu */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger data-testid="nav-services" className={triggerCls("services")}>
                    {navL.services}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[860px] p-5 grid grid-cols-3 gap-5">
                      {servicesGroups.map((g, gi) => {
                        const parentSlug = g.parent_slug || serviceSlugFor(g.title);
                        return (
                          <div key={gi} data-testid={`menu-service-group-${gi}`} className="min-w-0">
                            <div className="pb-2.5 mb-2.5 border-b border-slate-100">
                              <Link
                                to={`/services/${parentSlug}`}
                                className="font-heading font-semibold text-[13px] tracking-tight hover:text-[#1B6FE0] transition"
                                style={{ color: "#0F2A6E" }}
                                data-testid={`menu-service-group-title-${gi}`}
                              >
                                {g.title}
                              </Link>
                              <div className="text-[11.5px] text-[#6B7A8D] mt-0.5">{g.blurb}</div>
                            </div>
                            <ul className="space-y-1">
                              {(g.items || []).map((it, i) => {
                                const isObj = typeof it === "object" && it !== null;
                                const label = isObj ? it.title : it;
                                const subSlug = isObj ? it.slug : slugify(String(it));
                                return (
                                  <li key={i}>
                                    <Link
                                      to={`/services/${parentSlug}/${subSlug}`}
                                      data-testid={`menu-service-${gi}-${i}`}
                                      className="block w-full text-start text-[12.5px] text-[#6B7A8D] hover:text-[#1B6FE0] leading-relaxed transition py-0.5"
                                    >
                                      {label}
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Industries */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger data-testid="nav-industries" className={triggerCls("industries")}>
                    {navL.industries}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[460px] p-3 grid grid-cols-2 gap-1 max-h-[420px] overflow-y-auto">
                      {industriesItems.map((it, i) => (
                        <button
                          key={i}
                          data-testid={`menu-industry-${i}`}
                          onClick={() => scrollTo("industries")}
                          className="text-start p-2.5 rounded-md hover:bg-blue-50 transition"
                        >
                          <div className="text-[13px] font-medium text-[#0F2A6E]">{it.title}</div>
                        </button>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Portfolio + Resources removed per user request */}

                {/* Clients Portfolio */}
                <NavigationMenuItem>
                  <Link
                    to="/clients-portfolio"
                    data-testid="nav-clients-portfolio"
                    className={`${linkCls("clients-portfolio")} px-3 py-2 inline-flex items-center gap-1.5`}
                  >
                    {navL.clientsPortfolio}
                  </Link>
                </NavigationMenuItem>

                {/* Become a partner removed per user request */}

                {/* Contact link removed from desktop nav (the "Contact Us" CTA on the right is the single entry point) */}
              </NavigationMenuList>
            </NavigationMenu>

            {/* Language icon toggle — sits between nav and CTA */}
            <LangToggle lang={lang} setLang={setLang} isRTL={isRTL} />

            <Button
              data-testid="nav-cta-schedule"
              onClick={() => setConnectOpen(true)}
              className="hidden md:inline-flex h-10 px-5 text-white font-medium tracking-tight items-center gap-2"
              style={{ background: "#1B6FE0" }}
            >
              <Phone size={15} weight="fill" />
              {navL.scheduleCall}
            </Button>

            {/* Mobile menu */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-btn"
                  aria-label="Open menu"
                  className="lg:hidden p-2 rounded-md text-[#0F2A6E] hover:bg-slate-100"
                >
                  <List size={22} weight="regular" />
                </button>
              </SheetTrigger>
              <SheetContent side={isRTL ? "left" : "right"} className="w-[320px] overflow-y-auto">
                <MobileNav
                  navL={navL}
                  brandsItems={brandsItems}
                  servicesGroups={servicesGroups}
                  lang={lang}
                  setLang={setLang}
                  isRTL={isRTL}
                  scrollTo={scrollTo}
                  onClose={() => setOpen(false)}
                  openPartner={() => { setOpen(false); setTimeout(() => setPartnerOpen(true), 150); }}
                />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <BecomePartnerDialog open={partnerOpen} onOpenChange={setPartnerOpen} />
      <ConnectDialog open={connectOpen} onOpenChange={setConnectOpen} />
    </header>
  );
}
